import { useEffect, useRef, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Trash2, Plus, Upload, FileText } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-widest text-slate-600 font-medium">{label}</Label>
      {children}
    </div>
  );
}

const empty = { name: "", item_type: "", thickness: "", print_type: "", description: "", customer_name: "" };

export default function ArtworkMaster() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(empty);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => setRows((r => Array.isArray(r) ? r : [])((await api.get("/artwork-master")).data));
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/artwork", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedFile(data);
      toast.success(`Uploaded ${data.original_filename}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Artwork name is required"); return; }
    try {
      await api.post("/artwork-master", {
        ...form,
        file_id: uploadedFile?.id || null,
        storage_path: uploadedFile?.storage_path || "",
        original_filename: uploadedFile?.original_filename || "",
        size: uploadedFile?.size || 0,
        content_type: uploadedFile?.content_type || "",
      });
      setForm(empty);
      setUploadedFile(null);
      toast.success("Product saved");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/artwork-master/${confirmId}`);
      toast.success("Product deleted");
      load();
    } catch { toast.error("Failed to delete"); }
    finally { setDeleting(false); setConfirmId(null); }
  };

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.2em] text-yellow-600 font-semibold mb-1">Master</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Products</h1>
        <p className="text-slate-500 text-sm mt-2">Save products here — pick them from the dropdown when creating a job card so you never re-enter the same item twice. New items typed in a job card are auto-saved here.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-5 border-slate-200 lg:col-span-2">
          <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>All Products</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Thickness</th>
                  <th className="py-2 pr-3">Print Type</th>
                  <th className="py-2 pr-3">Customer</th>
                  <th className="py-2 pr-3">File</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">No products saved yet. Create a job card — items get auto-saved here.</td></tr>}
                {rows.map((a) => (
                  <tr key={a.id} className="border-b border-slate-100 align-middle">
                    <td className="py-2 pr-3 font-medium">{a.name}</td>
                    <td className="py-2 pr-3 text-slate-600">{a.item_type || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600">{a.thickness || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600">{a.print_type || "—"}</td>
                    <td className="py-2 pr-3 text-slate-600">{a.customer_name || "—"}</td>
                    <td className="py-2 pr-3">
                      {a.file_id ? (
                        <a href={`${API_BASE.replace("/api", "")}/uploads/${a.storage_path}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900 underline">
                          <FileText className="w-3 h-3" /> {a.original_filename}
                        </a>
                      ) : <span className="text-slate-400 text-xs">No file</span>}
                    </td>
                    <td className="py-2 text-right"><Button variant="ghost" size="icon" onClick={() => setConfirmId(a.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="text-lg font-semibold mb-3" style={{ fontFamily: "var(--font-heading)" }}>Add Product</div>
          <form onSubmit={submit} className="space-y-3">
            <Field label="Product Name *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Nike RFID Label" required />
            </Field>
            <Field label="Item Type">
              <Input value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })} placeholder="e.g. Sticker, Label, Tag" />
            </Field>
            <Field label="Thickness">
              <Input value={form.thickness} onChange={(e) => setForm({ ...form, thickness: e.target.value })} placeholder="e.g. 80 GSM" />
            </Field>
            <Field label="Print Type">
              <Input value={form.print_type} onChange={(e) => setForm({ ...form, print_type: e.target.value })} placeholder="e.g. CMYK, Flexo" />
            </Field>
            <Field label="Notes">
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Any notes" />
            </Field>
            <Field label="Product Image / File">
              <input ref={fileRef} type="file" onChange={handleFile} className="hidden" accept="application/pdf,image/*" />
              {uploadedFile ? (
                <div className="flex items-center gap-2 border border-slate-200 rounded p-2 bg-slate-50">
                  <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                  <span className="text-sm truncate flex-1">{uploadedFile.original_filename}</span>
                  <button type="button" onClick={() => setUploadedFile(null)} className="text-xs text-slate-500 hover:text-red-600">✕</button>
                </div>
              ) : (
                <Button type="button" variant="outline" className="w-full" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : "Upload Image / PDF"}
                </Button>
              )}
            </Field>
            <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" /> Save Product
            </Button>
          </form>
        </Card>
      </div>
      <ConfirmDialog
        open={!!confirmId}
        title="Delete Product?"
        description="This will permanently delete the product. This cannot be undone."
        confirmLabel="Delete Product"
        busy={deleting}
        onCancel={() => setConfirmId(null)}
        onConfirm={remove}
      />
    </Layout>
  );
}
